// Express.js integration
import { Request, Response, NextFunction } from 'express'
import { Session as AuthSession, Scope } from '../types'

export interface AuthMiddlewareOptions {
  publicRoutes?: string[]
  onUnauthenticated?: (req: Request, res: Response) => void
  onUnauthorized?: (req: Request, res: Response, session: AuthSession, requiredScopes: Scope[]) => void
}

export function authMiddleware(options: AuthMiddlewareOptions = {}) {
  const {
    publicRoutes = [],
    onUnauthenticated,
    onUnauthorized
  } = options

  return async (req: Request, res: Response, next: NextFunction) => {
    const pathname = req.path

    // Check if route is public
    if (isPublicRoute(pathname, publicRoutes)) {
      return next()
    }

    // Get session from cookie or header
    const session = await getSession(req)
    if (!session) {
      if (onUnauthenticated) {
        return onUnauthenticated(req, res)
      }
      return res.status(401).json({ error: 'Unauthorized' })
    }

    // Check if session is expired
    if (session.expiresAt < new Date()) {
      if (onUnauthenticated) {
        return onUnauthenticated(req, res)
      }
      return res.status(401).json({ error: 'Session expired' })
    }

    // Add session to request object (cast Express's session type)
    ;(req as any).kitiumSession = session
    next()
  }
}

export function requireScopes(requiredScopes: Scope[]) {
  return (req: Request, res: Response, next: NextFunction) => {
    const session = (req as any).kitiumSession as AuthSession
    if (!session) {
      return res.status(401).json({ error: 'No session' })
    }

    const hasRequiredScopes = requiredScopes.every(scope =>
      session.entitlements.includes(scope)
    )

    if (!hasRequiredScopes) {
      return res.status(403).json({ error: 'Insufficient permissions' })
    }

    next()
  }
}

export async function getSession(req: Request): Promise<AuthSession | null> {
  // Try to get from cookie first
  const sessionCookie = req.cookies?.['kitium.session']
  if (sessionCookie) {
    try {
      const sessionData = JSON.parse(decodeURIComponent(sessionCookie))

      // Validate session structure
      if (!sessionData.id || !sessionData.userId || !sessionData.expiresAt) {
        return null
      }

      // Check if session is expired
      if (new Date(sessionData.expiresAt) < new Date()) {
        return null
      }

      return sessionData as AuthSession
    } catch (error) {
      console.error('Failed to parse session cookie:', error)
    }
  }

  // Try to get from Authorization header (JWT token)
  const authHeader = req.headers.authorization
  if (authHeader?.startsWith('Bearer ')) {
    const token = authHeader.substring(7)
    
    try {
      // In a real implementation, you'd verify the JWT token with your auth core
      // For now, we'll return null to indicate no valid session
      return null
    } catch (error) {
      console.error('Failed to verify JWT token:', error)
      return null
    }
  }

  return null
}

export function isPublicRoute(pathname: string, publicRoutes: string[]): boolean {
  return publicRoutes.some(route => {
    if (route === '*') return true
    if (route.endsWith('*')) {
      return pathname.startsWith(route.slice(0, -1))
    }
    return pathname === route
  })
}

// Extend Express Request type
declare global {
  namespace Express {
    interface Request {
      kitiumSession?: AuthSession
    }
  }
}
